import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, errorText, formatDateTime, oeffneBeleg } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, Empty, Loading, Notice, SelectField, TextField } from '../components/ui';
import { AuthBild, FotoGalerie } from '../components/Foto';

interface ContainerRow {
  id: string;
  qr_token: string;
  iso_nummer: string | null;
  owner_name: string | null;
  typ: string | null;
  status: string;
  location_code: string | null;
  notiz: string | null;
  foto_anzahl: number;
  erstellt_am: string;
}

interface Bewegung {
  id: string;
  art: string;
  von_code: string | null;
  nach_code: string | null;
  erstellt_am: string;
}

type ContainerDetail = ContainerRow & { bewegungen: Bewegung[]; fotos: { id: string }[] };

interface Ort {
  id: string;
  code: string;
  name: string;
}

export function ContainerPage() {
  const { owners } = useAuth();
  const [liste, setListe] = useState<ContainerRow[] | null>(null);
  const [orte, setOrte] = useState<Ort[]>([]);
  const [detail, setDetail] = useState<ContainerDetail | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [fotoReload, setFotoReload] = useState(0);
  const dateiRef = useRef<HTMLInputElement>(null);

  // Neu-Formular
  const [ownerId, setOwnerId] = useState('');
  const [iso, setIso] = useState('');
  const [typ, setTyp] = useState('');
  const [ortId, setOrtId] = useState('');
  const [notiz, setNotiz] = useState('');
  const [zielId, setZielId] = useState('');

  const laden = useCallback(async () => {
    setError('');
    try {
      setListe(await api<ContainerRow[]>('/api/container'));
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void laden();
    api<Ort[]>('/api/warehouse/locations', { query: { includeVirtual: false } })
      .then(setOrte)
      .catch(() => {});
  }, [laden]);

  // Container aus der Kopfzeilen-Suche direkt öffnen (per QR-Token oder ISO-Nr).
  const [sp] = useSearchParams();
  useEffect(() => {
    const code = sp.get('code');
    if (!code) return;
    api<ContainerRow>(`/api/container/scan/${encodeURIComponent(code)}`)
      .then((c) => oeffne(c.id))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);


  async function oeffne(id: string) {
    setError('');
    try {
      setDetail(await api<ContainerDetail>(`/api/container/${id}`));
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function vereinnahmen() {
    setError('');
    setMessage('');
    // Containernummer wird vorerst nicht verifiziert - kein Blockieren.
    try {
      const c = await api<ContainerDetail>('/api/container', {
        method: 'POST',
        body: {
          ownerId: ownerId || null,
          isoNummer: iso.trim() || null,
          typ: typ.trim() || null,
          toLocationId: ortId || null,
          notiz: notiz.trim() || null,
        },
      });
      setMessage(`Container ${c.qr_token} vereinnahmt.`);
      setIso('');
      setTyp('');
      setNotiz('');
      await laden();
      setDetail(c);
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function bewegen(art: 'umlagerung' | 'auslagerung' | 'einlagerung') {
    if (!detail) return;
    if ((art === 'umlagerung' || art === 'einlagerung') && !zielId) {
      setError('Bitte Ziel-Lagerplatz wählen');
      return;
    }
    setError('');
    try {
      await api(`/api/container/${detail.id}/bewegung`, {
        method: 'POST',
        body: { art, toLocationId: art === 'auslagerung' ? null : zielId },
      });
      setMessage(`${art} gebucht.`);
      setZielId('');
      await laden();
      await oeffne(detail.id);
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function etikettOeffnen(id: string) {
    try {
      await oeffneBeleg(`/api/container/${id}/etikett`);
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function fotoHochladen(datei: File) {
    if (!detail) return;
    try {
      await api('/api/fotos', {
        method: 'POST',
        query: { refType: 'container', refId: detail.id },
        raw: { body: datei, contentType: datei.type || 'image/jpeg' },
      });
      setFotoReload((n) => n + 1);
      await laden();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr)', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 16 }}>
          <Card>
            <h3 style={{ marginTop: 0 }}>Container vereinnahmen</h3>
            <SelectField
              label="Firma (Eigentümer)"
              value={ownerId}
              onChange={setOwnerId}
              options={owners}
              render={(o) => o.name}
            />
            <TextField
              label="Containernummer (optional)"
              value={iso}
              onChange={setIso}
              placeholder="z. B. MSKU1234565"
              hint="Freie Eingabe – wird vorerst nicht geprüft"
            />
            <TextField label="Typ (optional)" value={typ} onChange={setTyp} placeholder="z. B. 40ft" />
            <SelectField
              label="Lagerplatz"
              value={ortId}
              onChange={setOrtId}
              options={orte}
              render={(o) => `${o.code} — ${o.name}`}
            />
            <TextField label="Notiz (optional)" value={notiz} onChange={setNotiz} />
            <button className="primary" onClick={() => void vereinnahmen()}>
              Vereinnahmen & QR erzeugen
            </button>
          </Card>

          <Card padded={false}>
            {!liste ? (
              <Loading />
            ) : liste.length === 0 ? (
              <Empty>Noch keine Container.</Empty>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Firma</th>
                      <th>Platz</th>
                      <th>Status</th>
                      <th className="num">Fotos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liste.map((c) => (
                      <tr
                        key={c.id}
                        onClick={() => void oeffne(c.id)}
                        style={{ cursor: 'pointer', background: detail?.id === c.id ? '#eef3f8' : undefined }}
                      >
                        <td className="mono">{c.qr_token}</td>
                        <td className="small">{c.owner_name ?? '—'}</td>
                        <td className="mono small">{c.location_code ?? '—'}</td>
                        <td>
                          <Badge kind={c.status === 'eingelagert' ? 'ok' : 'default'}>{c.status}</Badge>
                        </td>
                        <td className="num">{c.foto_anzahl || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        {detail && (
          <Card>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <AuthBild src={`/api/container/${detail.id}/qr.png`} alt="QR-Code" style={{ width: 120, height: 120 }} />
              <div>
                <div className="mono" style={{ fontSize: 24, fontWeight: 800 }}>{detail.qr_token}</div>
                <Badge kind={detail.status === 'eingelagert' ? 'ok' : 'default'}>{detail.status}</Badge>
                <div>
                  <button onClick={() => void etikettOeffnen(detail.id)}>Etikett (PDF)</button>
                </div>
              </div>
            </div>

            <dl className="kv">
              <dt>Firma</dt><dd>{detail.owner_name ?? '—'}</dd>
              <dt>Lagerplatz</dt><dd className="mono">{detail.location_code ?? '—'}</dd>
              <dt>ISO-Nummer</dt><dd className="mono">{detail.iso_nummer ?? '—'}</dd>
              <dt>Typ</dt><dd>{detail.typ ?? '—'}</dd>
              {detail.notiz ? (<><dt>Notiz</dt><dd>{detail.notiz}</dd></>) : null}
            </dl>

            <h4>Bewegen</h4>
            <SelectField label="Ziel-Lagerplatz" value={zielId} onChange={setZielId} options={orte} render={(o) => `${o.code} — ${o.name}`} />
            <div className="toolbar">
              {detail.status === 'eingelagert' ? (
                <>
                  <button className="primary" onClick={() => void bewegen('umlagerung')}>Umlagern</button>
                  <button onClick={() => void bewegen('auslagerung')}>Auslagern</button>
                </>
              ) : (
                <button className="primary" onClick={() => void bewegen('einlagerung')}>Wieder einlagern</button>
              )}
            </div>

            <h4>Fotos</h4>
            <FotoGalerie refType="container" refId={detail.id} reload={fotoReload} />
            <div>
              <input
                ref={dateiRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void fotoHochladen(f);
                  e.target.value = '';
                }}
              />
              <button onClick={() => dateiRef.current?.click()}>Foto hinzufügen</button>
            </div>

            <h4>Verlauf</h4>
            {detail.bewegungen.length === 0 ? (
              <Empty>Keine Bewegungen.</Empty>
            ) : (
              <ul className="small">
                {detail.bewegungen.map((b) => (
                  <li key={b.id}>
                    {b.art}
                    {b.nach_code ? ` → ${b.nach_code}` : ''}
                    {b.von_code ? ` (von ${b.von_code})` : ''} — {formatDateTime(b.erstellt_am)}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}
      </div>
    </>
  );
}
